import { useState } from 'react'

import { trailerConfig } from '@/components/TrailersTable'
import { truckConfig } from '@/components/TrucksTable'
import { Button } from '@/components/ui/button'
import type { Equipment } from '@/models/equipment'
import { type PolicyChangeInput, PolicyChangeKind } from '@/models/policyChange'
import { toIsoDate } from './policyChangeCopy'
import { type VehicleExtras, type VehicleKind, VehicleModal } from './VehicleModal'

interface AddVehicleFormProps {
  kind: VehicleKind
  /** The roster's vehicles, so the form catches a VIN already on it. */
  existing: Equipment[]
  /** Rejects with the API's error, which the form shows on its fields. */
  onAdd: (change: PolicyChangeInput) => Promise<void>
}

/** A truck or trailer to add, on the roster's own form, plus what only the insurance providers need. */
export function AddVehicleForm({ kind, existing, onAdd }: AddVehicleFormProps) {
  const config = kind === PolicyChangeKind.ADD_TRUCK ? truckConfig : trailerConfig
  const [vehicle, setVehicle] = useState<Equipment | null>(null)

  async function add(item: Equipment, extras: VehicleExtras) {
    await onAdd({
      kind,
      unit: {
        vehicleNumber: item.vehicleNumber,
        vin: item.vin,
        make: item.make,
        model: item.model,
        year: item.year || null,
        licensePlate: item.licensePlate,
        licenseState: item.licenseState,
        vehicleClass: item.vehicleClass,
        weightClass: item.weightClass,
        price: item.price,
      },
      details: { lossPayee: extras.lossPayee, garagingAddress: extras.garagingAddress },
      startDate: toIsoDate(extras.activeDate),
    })
    setVehicle(null)
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="self-start"
        onClick={() => setVehicle(config.createEmpty())}
      >
        Add {config.label}
      </Button>
      {vehicle && (
        <VehicleModal
          kind={kind}
          item={vehicle}
          isNew
          existing={existing}
          onClose={() => setVehicle(null)}
          onSubmit={add}
        />
      )}
    </>
  )
}
